import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getWidgetPreviewComponent } from './WidgetPreviews';
import { templatesAPI } from '../../services/api';
import { sampleReviews } from './SharedComponents';
import { getPlatformsWithIcons } from '../../utils/platformIcons';

// Widget preview component
const WidgetPreview = ({ templateId }) => {
    const PreviewComponent = getWidgetPreviewComponent(templateId);

    if (!PreviewComponent) {
        return (
            <div className="bg-gray-100 p-8 rounded-lg flex items-center justify-center">
                <p className="text-gray-500">Preview not available</p>
            </div>
        );
    }

    // Create default widget configuration to match Step 2 preview
    const getLayoutFromTemplateId = (templateId) => {
        const layoutMappings = {
            'carousel-widget': 'carousel',
            'simple-carousel': 'carousel',
            'reviews-carousel': 'carousel',
            'grid': 'grid',
            'mansory': 'mansory',
            'list': 'list',
            'sidebar-widget': 'sidebar',
            'badge': 'badge',
            'floating-badge': 'floating-badge',
            'slider': 'slider',
            // Dark theme variants removed - components handle theming internally
        };
        return layoutMappings[templateId] || 'carousel';
    };

    const getWidthFromTemplateId = (templateId) => {
        const widthMappings = {
            'carousel-widget': 1200,
            'simple-carousel': 1200,
            'reviews-carousel': 1200,
            'grid': 1200,
            'mansory': 1200,
            'list': 800,
            'sidebar-widget': 800,
            'badge': 400,
            'floating-badge': 400,
            'slider': 1200,
            // Dark theme variants removed - components handle theming internally
        };
        return widthMappings[templateId] || 1200;
    };

    const isDarkTheme = false; // Default to light theme, user can change in Step 2
    const isBadgeLayout = templateId === 'badge' || templateId === 'floating-badge';

    const defaultWidgetConfig = {
        selectedLayout: getLayoutFromTemplateId(templateId),
        badgePosition: 'bottom-right',
        // Widget-level configs (used directly by widget components)
        widgetTitleConfig: {
            showTitle: !isBadgeLayout,
            title: 'Customer Reviews',
            caption: 'See what our customers are saying'
        },
        headerConfig: {
            showHeader: !isBadgeLayout,
            elements: {
                rating: true,
                numberOfReviews: true,
                writeReviewButton: true,
                heading: true
            }
        },
        tabsConfig: {
            showTabs: !isBadgeLayout,
            elements: {
                icon: true,
                name: true,
                rating: true
            }
        },
        // Style configurations
        style: {
            colorScheme: isDarkTheme ? 'dark' : 'light',
            accentColor: '#4A90E2',
            fontFamily: 'default',
            background: {
                width: getWidthFromTemplateId(templateId),
                borderRadius: 16,
                shadow: true,
                color: isDarkTheme ? '#1F2937' : '#FFFFFF'
            },
            widgetTitle: {
                titleSize: 24,
                captionSize: 15,
                titleBold: false,
                titleItalic: false,
                captionBold: false,
                captionItalic: false,
                alignment: 'center'
            },
            rating: {
                starsColor: '#F1C40F'
            },
            review: {
                textSize: 16,
                cornerRadius: 8,
                outlineColor: isDarkTheme ? '#4B5563' : '#E5E5E5',
                backgroundColor: isDarkTheme ? '#374151' : '#F9FAFB',
                textColor: isDarkTheme ? '#FFFFFF' : '#000000',
                reviewerNameColor: isDarkTheme ? '#FFFFFF' : '#000000',
                dateColor: isDarkTheme ? '#D1D5DB' : '#666666'
            }
        }
    };

    // Create sample platform data for preview (matching Step2Configure.js structure)
    const samplePlatforms = ['google', 'facebook', 'amazon'];

    // Get platforms with icons (same as Step2Configure.js)
    const platformsWithIcons = getPlatformsWithIcons();

    // Create platform data with the same structure as Step2Configure.js getPlatformData()
    const sampleExternalPlatformData = platformsWithIcons.map(platform => {
        // Sample data for popular platforms, empty for others
        const sampleCounts = {
            'google': { count: 25, averageRating: 4.8 },
            'facebook': { count: 15, averageRating: 4.6 },
            'amazon': { count: 12, averageRating: 4.9 },
            'yelp': { count: 8, averageRating: 4.5 },
            'tripadvisor': { count: 6, averageRating: 4.7 }
        };

        const platformData = sampleCounts[platform.id] || { count: 0, averageRating: 0 };

        return {
            ...platform,
            count: platformData.count,
            averageRating: platformData.averageRating,
            hasReviews: platformData.count > 0
        };
    }).sort((a, b) => {
        // Sort same as Step2Configure.js: those with reviews first, then by count
        if (a.hasReviews && !b.hasReviews) return -1;
        if (!a.hasReviews && b.hasReviews) return 1;
        return b.count - a.count;
    });

    return (
        <PreviewComponent
            reviews={sampleReviews}
            loading={false}
            previewMode="desktop"
            selectedPlatforms={samplePlatforms}
            externalPlatformData={sampleExternalPlatformData}
            locationData={[]}
            layout={defaultWidgetConfig.selectedLayout}
            position={defaultWidgetConfig.badgePosition}
            widgetConfig={defaultWidgetConfig}
        />
    );
};

const Step1Layout = ({
    widgetConfig,
    onTemplateSelect,
    _previewMode,
    _setPreviewMode,
    _currentStep,
    onNext
}) => {
    const navigate = useNavigate();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch templates from API
    useEffect(() => {
        const fetchTemplates = async () => {
            try {
                setLoading(true);
                const response = await templatesAPI.getTemplatesByType('reviews');

                // Transform API response to match existing component structure
                const transformedTemplates = response.data.data.map(template => ({
                    id: template.templateSlug,
                    name: template.templateName,
                    image: template.templateImage,
                    category: template.category,
                    description: template.description,
                    features: template.features,
                    tags: template.tags,
                    _id: template._id // Keep MongoDB ID for reference
                }));

                setTemplates(transformedTemplates);
                setError(null);
            } catch (err) {
                console.error('Error fetching templates:', err);
                setError('Failed to load templates. Please try again.');

                // Fallback to static templates if API fails
                const fallbackTemplates = [
                    {
                        id: 'carousel-widget',
                        name: 'Carousel Widget',
                        image: '/review_widgets/Carousel Widget.png',
                        category: 'carousel'
                    },
                    {
                        id: 'grid',
                        name: 'Grid',
                        image: '/review_widgets/Grid.png',
                        category: 'grid'
                    },
                    {
                        id: 'badge',
                        name: 'Badge',
                        image: '/review_widgets/Badge.png',
                        category: 'badge'
                    }
                ];
                setTemplates(fallbackTemplates);
            } finally {
                setLoading(false);
            }
        };

        fetchTemplates();
    }, []);

    // Set first template as selected by default
    useEffect(() => {
        if (!widgetConfig.selectedTemplate && templates.length > 0) {
            onTemplateSelect(templates[0]);
        }
    }, [widgetConfig.selectedTemplate, onTemplateSelect, templates]);

    const handleBack = () => {
        navigate('/widgets');
    };

    const handleClose = () => {
        navigate('/dashboard');
    };

    const handleContinue = () => {
        if (onNext) {
            onNext();
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={handleBack}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <h1 className="text-xl font-semibold text-gray-900">Choose a Template</h1>
                    </div>
                    <button
                        onClick={handleClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            </div>

            <div className="flex h-[calc(100vh-73px)]">
                {/* Template Selection Sidebar */}
                <div className="w-80 bg-gray-900 text-white flex flex-col">
                    <div className="p-6 flex-shrink-0">
                        <h2 className="text-lg font-semibold mb-6">Select a template to start with</h2>
                    </div>

                    {/* Scrollable Template Grid */}
                    <div className="flex-1 overflow-y-auto px-6 pb-6">
                        {error && (
                            <div className="mb-4 p-3 bg-red-900/20 border border-red-800 rounded-lg">
                                <p className="text-red-300 text-sm">{error}</p>
                            </div>
                        )}

                        {loading ? (
                            <div className="grid grid-cols-2 gap-4 pt-2">
                                {/* Loading skeletons */}
                                {[...Array(6)].map((_, index) => (
                                    <div key={index} className="animate-pulse">
                                        <div className="bg-gray-800 rounded-lg overflow-hidden p-2">
                                            <div className="aspect-[4/3] w-full bg-gray-700 rounded"></div>
                                            <div className="mt-3 px-1">
                                                <div className="h-4 bg-gray-700 rounded w-3/4 mx-auto"></div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 gap-4 pt-2">
                                {templates.map((template) => {
                                    const isSelected = widgetConfig.selectedTemplate?.id === template.id;
                                    return (
                                        <div
                                            key={template.id}
                                            onClick={() => onTemplateSelect(template)}
                                            className={`cursor-pointer group relative rounded-lg transition-all duration-200 ${isSelected
                                                ? 'bg-blue-600/10 ring-2 ring-blue-400 ring-inset p-1'
                                                : 'hover:bg-gray-800/50 p-2'
                                                }`}
                                        >
                                            <div className={`relative bg-gray-800 rounded-lg overflow-hidden group-hover:bg-gray-700 transition-colors ${isSelected ? 'm-1' : ''}`}>
                                                <div className="aspect-[4/3] w-full">
                                                    <img
                                                        src={template.image}
                                                        alt={template.name}
                                                        className="w-full h-full object-cover"
                                                        onError={(e) => {
                                                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjE1MCIgdmlld0JveD0iMCAwIDIwMCAxNTAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMTUwIiBmaWxsPSIjMzc0MTUxIi8+Cjx0ZXh0IHg9IjEwMCIgeT0iNzUiIGZpbGw9IiM5Q0E0QUYiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxMiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSI+SW1hZ2UgTm90IEZvdW5kPC90ZXh0Pgo8L3N2Zz4K';
                                                        }}
                                                    />
                                                </div>
                                                {isSelected && (
                                                    <div className="absolute top-2 right-2">
                                                        <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center shadow-lg">
                                                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                            </svg>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="mt-3 px-1">
                                                <p className={`text-sm text-center transition-colors line-clamp-2 font-medium ${isSelected
                                                    ? 'text-blue-300'
                                                    : 'text-gray-300 group-hover:text-white'
                                                    }`}>
                                                    {template.name}
                                                </p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Continue Button - Fixed at Bottom */}
                    <div className="p-6 border-t border-gray-700 flex-shrink-0">
                        <button
                            onClick={handleContinue}
                            disabled={!widgetConfig.selectedTemplate}
                            className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${widgetConfig.selectedTemplate
                                ? 'bg-green-600 hover:bg-green-700 text-white'
                                : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                                }`}
                        >
                            Continue with this template
                        </button>
                    </div>
                </div>

                {/* Preview Area */}
                <div className="flex-1 overflow-y-auto">
                    <div className="p-8">
                        <div className="bg-white rounded-lg shadow-sm min-h-full">
                            {widgetConfig.selectedTemplate ? (
                                <div className="p-8">
                                    {/* Selected Template Preview */}
                                    <div className="mt-8">
                                        <WidgetPreview templateId={widgetConfig.selectedTemplate.id} />
                                        <div className="text-center mt-4">
                                            <h3 className="text-lg font-semibold text-gray-900">
                                                {widgetConfig.selectedTemplate.name}
                                            </h3>
                                            <p className="text-sm text-gray-500 capitalize">
                                                {widgetConfig.selectedTemplate.category} template
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="h-full flex items-center justify-center text-gray-500">
                                    <div className="text-center">
                                        <div className="w-16 h-16 bg-gray-200 rounded-lg mx-auto mb-4 flex items-center justify-center">
                                            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                        </div>
                                        <p className="text-lg font-medium mb-2">Select a template to see preview</p>
                                        <p className="text-sm text-gray-400">Choose from our collection of pre-designed templates</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Step1Layout; 